import React from 'react';
import {
  Card,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '0 solid #e2e8f0',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
}));

const TrendChip = styled(Chip)(({ trend }) => ({
  height: 30,
  fontSize: '0.85rem',
  fontWeight: 700,
  backgroundColor: '#fff',
  color: '#1f1f1f',
  border: '0 solid #e2e8f0',
  borderRadius: 6,
  paddingLeft: 2,
  paddingRight: 2,
  '& .MuiChip-icon': {
    color: '#1f1f1f',
  },
}));

const MetricCard = ({
  title,
  value,
  description,
  icon,
  change,
  trend,
  trendText,
  iconBgColor,
  breakdown,
  invertColors = false,
  loading = false,
  ...props
}) => {
  const isPositiveTrend = trend === 'up';
  const changeValue = change?.replace(/[^\d.-]/g, '') || '0';
  const changeSign = change?.includes('+') ? '+' : change?.includes('-') ? '-' : '';
  const numericChange = parseFloat(changeValue);
  const isZeroChange = !isNaN(numericChange) && Math.abs(numericChange) === 0;

  const green = '#10b981';
  const red = '#ef4444';
  const chipColor = isZeroChange
    ? '#6b7280'
    : (isPositiveTrend ? (invertColors ? red : green) : (invertColors ? green : red));

  // Infer no-current-data at card level: if value parses to 0 while footer implies a previous comparison
  const parseNumeric = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    const n = parseFloat(String(val).replace(/[^\d.-]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const parsedCurrent = parseNumeric(value);
  const footerImpliesPrev = typeof trendText === 'string' && /vs previous period/i.test(trendText);
  const noCurrentData = parsedCurrent === 0 && footerImpliesPrev;
  const displayValue = noCurrentData ? '—' : value;
  const showTrendChip = !!change && !noCurrentData;
  const displayFooter = noCurrentData ? 'No data in selected period' : trendText;

  return (
    <StyledCard
      {...props}
      role="article"
      aria-label={`${title} metric card`}
      tabIndex={0}
      sx={{
        ...props.sx,
        '&:focus': {
          outline: '1px solid #1f1f1f',
          outlineOffset: '2px',
        }
      }}
    >
      <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',gap: 1 }}>
        <Box sx={{ display: 'flex',alignItems: 'center',mb: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#020817',
                lineHeight: 1,
              }}
            >
              {displayValue}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'start', }}>
            {showTrendChip && (
              <TrendChip
                trend={isPositiveTrend ? 'up' : 'down'}
                icon={
                  isPositiveTrend ? (
                    <Box component="span" sx={{ fontSize: '0.65rem', color: chipColor }} aria-hidden="true">▲</Box>
                  ) : (
                    <Box component="span" sx={{ fontSize: '0.65rem', color: chipColor }} aria-hidden="true">▼</Box>
                  )
                }
                label={`${changeSign}${changeValue}%`}
                sx={{
                  color: chipColor,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { color: chipColor },
                }}
              />
            )}
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            color: '#020817',
            fontWeight: 600,
            lineHeight: 1.25,
            fontSize: '0.875rem',
            mb: 0.5,
          }}
          component="h3"
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#6b7280',
            fontWeight: 500,
            lineHeight: 1.25,
            fontSize: '0.75rem',
          }}
        >
          {description}
        </Typography>

        {breakdown && (
          <Box
            sx={{
              pt: 1,
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              overflowX: 'auto',
              pb: 0.5,
              // Hide scrollbar for Webkit browsers
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#e2e8f0',
                borderRadius: 3,
              },
              // Optional: Hide scrollbar for Firefox
              scrollbarWidth: 'thin',
              scrollbarColor: '#e2e8f0 transparent',
            }}
          >
            {breakdown.map((item, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: 80,
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  backgroundColor: '#f9fafb',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#737373',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {displayFooter && (
          <Typography
            variant="caption"
            sx={{
              color: '#6b7280',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.8rem',
              gap: 0.5,
              mt: breakdown ? 0.5 : 1,
            }}
          >
            {displayFooter.replace(/^Δ\s*/, '')}
          </Typography>
        )}
      </Box>
    </StyledCard>
  );
};

export default MetricCard;